import { FlosIcon, LoadingDots, SelectableBoxField } from '@flos/react-ui';
import { useContext, useEffect, useState } from 'react';
import { ConfigContext } from '../../../context/ConfigContext';
import useAppState from '../../../state/appState';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';
import {
    CustomerVehicle as CustomerVehicleType,
    FormConfig,
    FormValues,
    Coverage
} from '@topdanmark-onlinesolutions/claim-form-types';
import { EngagementVehicle, EntityType, InsuranceDetails, VehicleData } from '../../../types/services';

import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../../helpers/templates';

import useService from '../../../hooks/useService';
import { getInsuranceCoveragesServiceConfig } from '../../../services/insurances';
import { getVehicleServiceConfig } from '../../../services/vehicles';
import { Status } from '../../../types/status';
import isNotEmpty from '../../../validators/is-not-empty';
import { getValue } from '../../values/Get';
import VehicleTypeSelect from './VehicleTypeSelect';
import { OTHERS, iconMapping, mapToOptions } from './mapping';
import './style.scss';

type ComponentValue = {
    hasVehiclesInSystem: 'yes' | 'no';
    policyNrOrOthers: string;
    entityType: string;
    displayValue: string;
    registrationNumber: string;
    coverage: Coverage[] | undefined;
};

const CustomerVehicle = ({ caption, elementId }: CustomerVehicleType) => {
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const [vehicles, setVehicles] = useState<EngagementVehicle[] | undefined>(undefined);

    const appConfig = useContext(ConfigContext);
    const value = CustomerVehicle.getValueOrDefault(formValues, elementId);

    const { data: vehicleData, status: vehicleStatus } = useService<VehicleData>(getVehicleServiceConfig(appConfig));
    const { status: insuranceStatus, callService: fetchInsuranceCoverages } = useService<InsuranceDetails>(
        {},
        false,
        false
    );

    const options = mapToOptions(vehicles);

    useEffect(() => {
        if (Array.isArray(vehicleData)) {
            // Filter vehicle which has no description
            // It's possible that the description property is not in the repsonse!
            const filteredList = vehicleData.filter((vehicle: EngagementVehicle) => vehicle.description !== undefined);

            const list: EngagementVehicle[] = filteredList.map((vehicle: EngagementVehicle) => {
                const descriptionParts = vehicle.description.split(', ');
                return {
                    ...vehicle,
                    carMake: descriptionParts[1],
                    description: vehicle.description,
                    entity: vehicle.entity?.toLowerCase() as EntityType,
                    registrationNumber: descriptionParts[0]
                };
            });
            setVehicles(list);
        }
    }, [vehicleData]);

    const onChangeValue = ({
        hasVehiclesInSystem,
        policyNrOrOthers,
        entityType,
        displayValue,
        registrationNumber,
        coverage
    }: ComponentValue) => {
        setFormValue(elementId, {
            hasVehiclesInSystem,
            policyNrOrOthers,
            entityType,
            displayValue,
            registrationNumber,
            coverage
        });
    };

    /**
     * Get list
     * @param status
     * @param vehicles
     * @returns
     */
    const renderComponent = (
        status: { vehicles: Status; insurance: Status },
        vehicles: EngagementVehicle[] | undefined
    ) => {
        switch (status.vehicles && status.insurance) {
            case Status.LOADING:
                return (
                    <div className="u-spacing-inset-5">
                        <LoadingDots centered={false} />
                    </div>
                );
            case Status.ERROR:
                return <p>Der er desværre sket en teknisk fejl</p>;
            default:
                if (Array.isArray(vehicles) && vehicles.length > 0) {
                    const vehicleOptions: {
                        label: any;
                        value: string;
                        entityType: string;
                        registrationNumber: string;
                    }[] = [];

                    let activeValue = '';
                    options.forEach((option) => {
                        const entityType = option.entity;
                        const policyNrOrOthers = option.value;
                        const displayValue = option.description;
                        const registrationNumber = option.registrationNumber;

                        const svgIcon = iconMapping(entityType);
                        const label = (
                            <>
                                {svgIcon && <FlosIcon shape={svgIcon} size="large" />} {displayValue}
                            </>
                        );

                        vehicleOptions.push({
                            label,
                            value: policyNrOrOthers,
                            entityType,
                            registrationNumber
                        });

                        if (policyNrOrOthers === value.policyNrOrOthers) {
                            activeValue = policyNrOrOthers;
                        }
                    });

                    const handleChange = async (chosenValue: string) => {
                        const insuranceDetails =
                            chosenValue !== OTHERS
                                ? await fetchInsuranceCoverages(
                                      getInsuranceCoveragesServiceConfig(appConfig, chosenValue),
                                      false
                                  )
                                : undefined;

                        options.forEach((option) => {
                            const entityType = option.entity;
                            const policyNrOrOthers = option.value;
                            const displayValue = option.description;
                            const registrationNumber = option.registrationNumber;
                            if (chosenValue === policyNrOrOthers) {
                                onChangeValue({
                                    hasVehiclesInSystem: 'yes',
                                    policyNrOrOthers,
                                    entityType,
                                    displayValue,
                                    registrationNumber,
                                    coverage: insuranceDetails?.coverages
                                });
                            }
                        });
                    };

                    return (
                        <SelectableBoxField value={activeValue} onChangeValue={handleChange} options={vehicleOptions} />
                    );
                } else {
                    return (
                        <VehicleTypeSelect
                            label=""
                            value={value.entityType}
                            onChangeValue={(newEntity, newLabel) =>
                                onChangeValue({
                                    ...value,
                                    hasVehiclesInSystem: 'no',
                                    entityType: newEntity as EntityType,
                                    displayValue: newLabel
                                })
                            }
                        />
                    );
                }
        }
    };
    return (
        <div>
            {caption && <h6>{caption}</h6>}
            <div>{renderComponent({ insurance: insuranceStatus, vehicles: vehicleStatus }, vehicles)}</div>
        </div>
    );
};

CustomerVehicle.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as CustomerVehicleType;
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);

    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value.displayValue} />;
};

CustomerVehicle.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue => {
    const valueObj = CustomerVehicle.getValue(formValues, elementId);
    const valueStr = CustomerVehicle.getValueHuman(formValues, elementId);
    const valueId = {
        registrationNumber: valueObj.registrationNumber,
        policy: valueObj.policyNrOrOthers
    };
    return getClaimFormSubmitValue('customervehicle', elementId, caption, valueStr, valueId);
};

CustomerVehicle.getValueHuman = (formValues: FormValues, elementId: string) => {
    const value: ComponentValue = CustomerVehicle.getValue(formValues, elementId);
    if (value.hasVehiclesInSystem === 'yes' && value.registrationNumber !== '') {
        return value.registrationNumber;
    } else {
        return value.displayValue;
    }
};

CustomerVehicle.getValueOrDefault = (formValues: FormValues, elementId: string): ComponentValue => {
    const valueObj: ComponentValue = CustomerVehicle.getValue(formValues, elementId);

    if (valueObj === undefined) {
        return {
            hasVehiclesInSystem: 'no',
            policyNrOrOthers: '',
            entityType: '',
            displayValue: '',
            registrationNumber: '',
            coverage: undefined
        };
    }

    return valueObj;
};

CustomerVehicle.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const valueObject: ComponentValue = getValue(formConfig, formValues, elementId);

    if (!valueObject) {
        return false;
    }

    // Vehicle with policy
    if (valueObject.hasVehiclesInSystem === 'yes') {
        // entityType && displayValue && policyNrOrOthers is required
        if (
            !isNotEmpty(valueObject.entityType) ||
            !isNotEmpty(valueObject.displayValue) ||
            !isNotEmpty(valueObject.policyNrOrOthers)
        ) {
            return false;
        }

        // If others is selected just return true
        if (valueObject.entityType === 'others') {
            return true;
        }

        // If registrationNumber is not empty return true
        if (isNotEmpty(valueObject.registrationNumber)) {
            return true;
        }
    } else {
        // Vehicle without policy
        const validValues: string[] = ['personbil', 'knallert', 'trailer', 'campingvgn', 'others'];
        if (validValues.includes(valueObject.entityType)) {
            return true;
        }
    }

    return false;
};

CustomerVehicle.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

export default CustomerVehicle;
